import clsx from 'clsx';
import { HTMLAttributes } from 'react';

import countryCodesAlpha3 from './country-iso-alpha-three.json';
import countryCodes from './country-iso-codes.json';
import styles from './FlagIcon.module.scss';
import regionalCodes from './regional-codes.json';
import { convertCountryCodeToAlpha2, isRegionalCode } from './utils';

const COUNTRY_CODE_ALIASES: Record<string, string> = {
  UK: 'GB',
  SWI: 'CH',
};

const transformCountryCode = (countryCode: string) => {
  const upperCaseCountryCode = countryCode.toUpperCase();

  return COUNTRY_CODE_ALIASES[upperCaseCountryCode] ?? upperCaseCountryCode;
};

export interface FlagIconProps extends HTMLAttributes<HTMLSpanElement> {
  countryCode?: string;
  variant?: 'default' | 'rounded' | 'circular';
  customSize?: number;
}

// Direct dependency to "https://cdn.jsdelivr.net/npm/flag-icons/css/flag-icons.min.css"
export const FlagIcon = ({
  countryCode = '',
  variant = 'default',
  customSize = 14,
  ...rest
}: FlagIconProps) => {
  const { className, style, ...otherProps } = rest;
  const transformedCountryCode = transformCountryCode(countryCode);

  if (!countryCode) {
    return null;
  }

  const isValidCode =
    countryCodesAlpha3.includes(transformedCountryCode) ||
    countryCodes.includes(transformedCountryCode) ||
    regionalCodes.includes(transformedCountryCode);

  if (!isValidCode) {
    return (
      <span
        className={clsx(styles.fallback, className)}
        style={{ minHeight: `${customSize}px` }}
        data-testid="fallback"
        {...rest}
      >
        {transformedCountryCode}
      </span>
    );
  }

  const alpha2Code = convertCountryCodeToAlpha2(transformedCountryCode);

  if (!alpha2Code || isRegionalCode(transformedCountryCode)) {
    return (
      <span
        className={clsx(styles.fallback, className)}
        style={{ minHeight: `${customSize}px` }}
        data-testid="fallback"
        {...rest}
      >
        {transformedCountryCode}
      </span>
    );
  }

  const flagClasses = clsx(
    styles.flag,
    'fi',
    `fi-${alpha2Code.toLowerCase()}`,
    variant === 'circular' && 'fis',
    variant !== 'default' && styles[variant],
    className
  );

  const flagStyle = {
    height: `${customSize}px`,
    width: `${variant === 'circular' ? customSize : customSize * 1.5}px`,
  };

  return (
    <span
      className={flagClasses}
      style={flagStyle}
      data-country={transformedCountryCode}
      {...otherProps}
    />
  );
};
